import { useCallback, useState } from "react";
import { t } from "i18next";
import { useNavigate, useOutletContext } from "react-router";

import ConfirmationModal from "@components/shared/ConfirmationModal/ConfirmationModal";
import PasswordLabel from "@components/shared/FormElements/InputPassword/InputPassword";
import Loader from "@components/shared/Loader/Loader";
import { CONSTANTS } from "@constants/index";
import { userProfileData } from "@constants/user";
import {
  DeleteAccountModalProp,
  ProfileData,
} from "@models/components/user.model";
import { LocalizationObjectType } from "@models/utils/localisation.models";
import handleResponse from "@services/apiConfig/api.response.service";
import { applyLocalization } from "@services/common.service";
import API_SERVICES from "@services/index";
import commonUtils from "@utils/common.utils";
import { logout } from "@utils/user.util";

import ProfileDataBlock from "../ProfileDataBlock/ProfileDataBlock";
import ProfileInfoEdit from "../ProfileInfoEdit/ProfileInfoEdit";

export const ProfileInfo = () => {
  const [editForm, setEditForm] = useState(false);
  const [loader, setLoader] = useState(false);
  const [password, setPassword] = useState("");
  const [confirmationModalState, setConfirmationModalState] =
    useState<DeleteAccountModalProp>({
      bodyText: "",
      isDeletable: false,
      isOpen: false,
    });

  const navigate = useNavigate();
  const [data, getUserProfile]: [ProfileData, () => void] = useOutletContext();
  const { firstSectionItems, lastSectionItems, secondSectionItems } =
    userProfileData(data);

  function closeForm() {
    setEditForm(!editForm);
  }

  function setModalBodyText(deletePossible: string, clubs: []) {
    if (deletePossible) {
      return (
        <>
          <div className="alert alert-warning">
            {t("deleteAccountContinue")}
          </div>
          <div className="form-floating mb-3 fg-input-with-icon">
            <PasswordLabel
              isIcon={true}
              labelText={t("passwordLabel")}
              onChange={e => setPassword(e.target.value)}
              testId="password"
              isError={false}
              showStrength={false}
              autoComplete="new-password"
            />
          </div>
        </>
      );
    }
    return (
      <>
        <span className="pb-5">{t("deleteAccountRestrictMsg")}</span>
        <ul className="pt-2 fg-modal-account-delete-club-list">
          {clubs.map((club: LocalizationObjectType, index: number) => (
            <li key={index}>
              <span>{applyLocalization(club)}</span>
            </li>
          ))}
        </ul>
      </>
    );
  }

  const fetchDeleteAccountStatus = useCallback(async () => {
    setLoader(true);
    try {
      const response = await API_SERVICES.USER.CHECK_ACCOUNT_DELETE();
      const responseData = handleResponse(response);

      const deletePossible = responseData?.data?.deletePossible || false;
      const clubs = responseData?.data?.paymentPendingClubs || [];

      setConfirmationModalState({
        bodyText: setModalBodyText(deletePossible, clubs),
        isDeletable: deletePossible,
        isOpen: true,
      });
    } finally {
      setLoader(false);
    }
  }, []);

  const deleteAccount = useCallback(async () => {
    setLoader(true);
    try {
      const response = await API_SERVICES.USER.ACCOUNT_DELETE(password);
      const responseData = handleResponse(response);

      if (responseData?.ok) {
        commonUtils?.toastService(t("accountDeletedSuccessfully"));
        updateModalState();
        logout(() => navigate("/"), false);
      }
    } catch {
      commonUtils?.toastService(
        t("accountDeleteFailed"),
        CONSTANTS.TOAST_TYPE.ERROR
      );
    } finally {
      setLoader(false);
    }
  }, [password]);
  const updateModalState = () =>
    setConfirmationModalState(prev => ({
      ...prev,
      isOpen: !confirmationModalState.isOpen,
    }));

  return (
    <>
      <div
        className="tab-pane fade show active"
        id="pills-info"
        role="tabpanel"
        data-testid="profile-info-test"
      >
        <div className="fg-tab-content-wrap">
          <div className="container">
            {!editForm ? (
              <>
                <div className="row fg-profile-contact-section">
                  <ProfileDataBlock
                    contactType={data?.profile?.contactType}
                    customClassName="col-12 col-sm-6 col-md-6 col-lg-4"
                    data={firstSectionItems}
                  />
                  <ProfileDataBlock
                    contactType={data?.profile?.contactType}
                    customClassName="col-12 col-sm-6 col-md-6 col-lg-4"
                    data={secondSectionItems}
                  />
                  <ProfileDataBlock
                    contactType={data?.profile?.contactType}
                    customClassName="col-12 col-lg-3 col-md-6 col-sm-6 offset-md-6 offset-lg-0 offset-sm-6"
                    data={lastSectionItems}
                  />
                  <div className="col-auto">
                    <button
                      className="btn fg-icon-btn ms-auto d-block fg-padding-10 fg-edit-profile-btn"
                      onClick={() => setEditForm(!editForm)}
                      data-testid="user-info-edit"
                    >
                      <i className="fa-solid fa-pen-to-square fg-text-primary-main"></i>
                    </button>
                  </div>
                </div>
                <button
                  type="button"
                  data-testid="user-account-delete"
                  className="btn btn-danger mt-2"
                  onClick={fetchDeleteAccountStatus}
                >
                  {t("deleteMyAccount")}
                </button>
              </>
            ) : (
              <ProfileInfoEdit
                getUserProfile={getUserProfile}
                data={data}
                closeForm={closeForm}
              />
            )}
          </div>
        </div>
      </div>

      <Loader isLoading={loader} />

      <ConfirmationModal
        isOpen={confirmationModalState.isOpen}
        updateModalState={updateModalState}
        submitAction={deleteAccount}
        modalHeaderText={t("deleteMyAccount")}
        modalBodyText={confirmationModalState.bodyText}
        showSubmitButton={confirmationModalState.isDeletable}
        submitButtonText={t("deleteMyAccount")}
        cancelButtonText={
          confirmationModalState.isDeletable
            ? t("cancel")
            : t("deleteAccountCloseButton")
        }
        isLoading={loader}
        submitButtonDisabled={!password}
      />
    </>
  );
};
